import moment from 'moment';
import { Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Divider, ProfessionalHeader } from '~/commonComponents';
import { ProfessionalBestInDentalWinnerTooltip } from '~/components/BestInDental';
import { BestInDentalStatus } from '~/enums/BestInDental';
import { useCandidate } from '~/hooks/useCandidate';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import { IJob, IUser } from '~/models';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { EducationUtils } from '~/utils/EducationUtils';
import { ProfessionalInfoSection } from './components/ProfileInfoSection';
import { VerifiedCredentials } from './components/VerifiedCredentials';
import {
  DnDaProcedures,
  ProfessionalAttestationInfo,
  RdhFdProcedures,
} from '~/components/CandidateOverview/components';

interface IProfileCardProps {
  professional: IUser;
  job: IJob | null;
  displayProfession?: string;
  displayProfessionName?: string;
  displayLicenseNumber?: string;
  authorizations?: IAuthorization[];
  isCredentialV2?: boolean;
}

export const ProfileCard = ({
  professional,
  job,
  displayProfession,
  displayProfessionName,
  displayLicenseNumber,
  authorizations = [],
  isCredentialV2 = false,
}: IProfileCardProps) => {
  const user = useSelectedOffice(job?.offer_owner?.id);
  const {
    specialtyMatch,
    insuranceMismatch,
    userSpecialties,
    softwares,
    licenseNumber,
    licenseState,
    issueDate,
    nationalProviderIdentifier,
    shouldShowNpi,
    professionName,
    candidateInsurances,
    yearsOfExperience,
    candidateOffer,
  } = useCandidate(user, professional, job);

  const isBestInDentalWinner =
    professional?.professional_info?.bestInDental?.status === BestInDentalStatus.Winner;

  return (
    <Column
      pt={6}
      pb={8}
      px={8}
      style={{
        position: 'relative',
        backgroundColor: colors.shades[0],
        borderRadius: 8,
        boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
      }}
    >
      {isBestInDentalWinner && <ProfessionalBestInDentalWinnerTooltip />}

      <ProfessionalHeader professional={professional} displayProfession={displayProfession} />

      <Divider height={1} />

      <Column gap={5}>
        <ProfessionalAttestationInfo consent={candidateOffer?.consent} />

        <ProfessionalInfoSection
          title="Education"
          value={
            <Typography as="span">
              {professional?.license?.school}
              <br />
              Graduation Date: {moment(professional?.license?.graduationDate).format('MM/DD/YYYY')}
            </Typography>
          }
        />
        <ProfessionalInfoSection title="Profession" value={displayProfessionName || professionName} />

        {!isCredentialV2 && (
          <Column gap={1}>
            <ProfessionalInfoSection title="License #" isSameLine value={displayLicenseNumber || licenseNumber || '-'} />
            <ProfessionalInfoSection
              title="Issued by State of"
              isSameLine
              value={licenseState || '-'}
            />
            <ProfessionalInfoSection
              title="Year Issued"
              isSameLine
              value={issueDate ? moment(issueDate).format('YYYY') : '-'}
            />
          </Column>
        )}

        {isCredentialV2 && (
          <VerifiedCredentials authorizations={authorizations} />
        )}

        {!isCredentialV2 && nationalProviderIdentifier && shouldShowNpi && (
          <ProfessionalInfoSection title="NPI" value={nationalProviderIdentifier} />
        )}

        <ProfessionalInfoSection title="Years of Experience" value={yearsOfExperience} />

        <ProfessionalInfoSection
          title="Software"
          value={softwares?.join(', ')}
          showWarningIcon={job ? !specialtyMatch || !!insuranceMismatch : false}
        />

        {shouldShowNpi && (
          <ProfessionalInfoSection
            title="Insurance"
            value={candidateInsurances?.join(', ')}
            showWarningIcon={job ? !!insuranceMismatch : false}
          />
        )}

        <ProfessionalInfoSection
          title="Specialty(ies)"
          value={EducationUtils.mapToRemoveSpecialtyPrefix(userSpecialties).join(', ')}
          showWarningIcon={job ? !specialtyMatch || !!insuranceMismatch : false}
        />

        <Column width="100%">
          <Typography as="span" bold size="heading_4">
            Experienced Procedures:
          </Typography>
          <RdhFdProcedures professional={professional} job={job} />
          <DnDaProcedures professional={professional} job={job} />
        </Column>
      </Column>
    </Column>
  );
};
